import { Result, err, ok } from 'neverthrow'
import { getChatApiRoute } from '@renderer/chat/api/v1Client'
import { runSendChallenge } from '@renderer/chat/hooks/send-challenge'
import FlagsIpcRenderer from '@renderer/ipc/FlagsIpc.renderer'
import type { TurnTraceEvent } from '@shared/chat-telemetry/turn-events'
import GlobalStateUtils from '@shared/ipc/GlobalStateUtils'
import { deviceIdHeader } from '@shared/store/impl/device'
import GlobalLogger from '@shared/utils/logger/GlobalLogger'
import { VERIFICATION_REQUIRED_ERROR_CODE, connectToTurnStream, type ChatTurnError } from '../engine/errors'
import type { ChatRequestBody } from '../engine/services'
import { traceTurnEvent } from '../engine/telemetry'

export type ChatTurnPost = {
  readonly chatId: string
  readonly turnId: string | undefined
  readonly body: ChatRequestBody
  readonly signal: AbortSignal
  readonly getAuthToken: () => Promise<string>
  readonly getByokHeaders: () => Record<string, string>
  /** This renderer's copy of the challenge flag. The gateway's copy decides whether a token is required. */
  readonly isSendChallengeEnabled: boolean
  /** A dev build opted out with `DISABLE_CAPTCHA`. No challenge runs, even when the gateway asks for one. */
  readonly isChallengeOptedOut: boolean
}

type RetryOutcome = Extract<TurnTraceEvent, { kind: 'challenge:retry' }>['outcome']

/**
 * The gateway refused a turn of this user's for want of a token. Later sends
 * run the challenge up front, so a stale local flag costs one refused POST,
 * not one per turn.
 */
let hasGatewayRequiredToken = false

/** The local flag as the last send saw it, to catch the kill switch turning it off. */
let wasSendChallengeEnabled = false

GlobalStateUtils.user.onUserIdChange(() => {
  hasGatewayRequiredToken = false
})

/**
 * POST a chat turn with the Arkose token it needs. If the gateway refuses it
 * for verification, run the challenge again and post once more, even with the
 * local flag off. The gateway only asks for a token when its own flag is on.
 * Its other abuse gates answer with the same refusal, so they cost one wasted
 * challenge before the error shows.
 */
export async function postChatTurn(post: ChatTurnPost): Promise<Result<Response, ChatTurnError>> {
  trackLocalFlag(post.isSendChallengeEnabled)
  const token = await getUpFrontToken(post)
  if (token.isErr()) {
    return err(token.error)
  }

  const sent = await sendChatTurn(post, token.value)
  if (post.isChallengeOptedOut || sent.isErr() || !(await isVerificationRefusal(sent.value))) {
    return sent
  }
  const refusedPostHadToken = token.value !== undefined
  rememberGatewayRequiresToken(refusedPostHadToken)
  return await resendWithRequiredChallenge(post, refusedPostHadToken)
}

/**
 * The local flag turning off is the kill switch, and it outranks a refusal
 * remembered while the flag was on. A wipe looks the same; it costs one more
 * refused POST before the requirement is remembered again.
 */
function trackLocalFlag(isSendChallengeEnabled: boolean) {
  if (wasSendChallengeEnabled && !isSendChallengeEnabled) {
    hasGatewayRequiredToken = false
  }
  wasSendChallengeEnabled = isSendChallengeEnabled
}

async function getUpFrontToken(post: ChatTurnPost): Promise<Result<string | undefined, ChatTurnError>> {
  if (post.isChallengeOptedOut) {
    return ok(undefined)
  }
  if (post.isSendChallengeEnabled) {
    return await runSendChallenge(challengeParams(post))
  }
  if (hasGatewayRequiredToken) {
    return await runSendChallenge({ ...challengeParams(post), gatewayRequiresToken: true })
  }
  return ok(undefined)
}

function rememberGatewayRequiresToken(refusedPostHadToken: boolean) {
  hasGatewayRequiredToken = true
  if (refusedPostHadToken) {
    return
  }
  // The local flag is stale. A fresh copy brings back the prefetch and the warm window.
  FlagsIpcRenderer.refresh().catch((cause: unknown) => {
    GlobalLogger.warn(`Failed to refresh flags after a verification refusal: ${String(cause)}`)
  })
}

async function resendWithRequiredChallenge(
  post: ChatTurnPost,
  refusedPostHadToken: boolean,
): Promise<Result<Response, ChatTurnError>> {
  const token = await runSendChallenge({ ...challengeParams(post), gatewayRequiresToken: true })
  if (token.isErr()) {
    traceRetry(post, refusedPostHadToken, post.signal.aborted ? 'cancelled' : 'challenge-failed')
    return err(token.error)
  }

  const resent = await sendChatTurn(post, token.value)
  const outcome = await describeResend(post.signal, resent)
  if (outcome === 'refused-again') {
    // A fresh token was refused too, so the refusal isn't one an up-front challenge fixes.
    hasGatewayRequiredToken = false
  }
  traceRetry(post, refusedPostHadToken, outcome)
  return resent
}

async function describeResend(signal: AbortSignal, resent: Result<Response, ChatTurnError>): Promise<RetryOutcome> {
  if (signal.aborted) {
    return 'cancelled'
  }
  if (resent.isErr()) {
    return 'resend-failed'
  }
  if (resent.value.ok) {
    return 'resent'
  }
  return (await isVerificationRefusal(resent.value)) ? 'refused-again' : 'resend-failed'
}

function challengeParams({ chatId, turnId, signal, getAuthToken }: ChatTurnPost) {
  return { chatId, turnId, signal, getAuthToken }
}

async function sendChatTurn(post: ChatTurnPost, arkoseToken: string | undefined) {
  // Inside the wrapper so an auth failure lands on the Err channel too.
  return await connectToTurnStream({ phase: 'submit', chatId: post.chatId }, async () => {
    const accessToken = await post.getAuthToken()
    return fetch(getChatApiRoute('api/v1/chat'), {
      method: 'POST',
      body: JSON.stringify(post.body),
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Arkose-Session-Token': arkoseToken ?? '',
        ...deviceIdHeader(),
        ...post.getByokHeaders(),
      },
      signal: post.signal,
    })
  })
}

/** Reads a copy of the body, so the engine can still read the original. */
async function isVerificationRefusal(response: Response): Promise<boolean> {
  if (response.status !== 403) {
    return false
  }
  // A body that isn't JSON isn't this refusal; the engine reports it as it is.
  const body = (await response
    .clone()
    .json()
    .catch(() => null)) as { errorCode?: unknown } | null
  return body?.errorCode === VERIFICATION_REQUIRED_ERROR_CODE
}

function traceRetry(post: ChatTurnPost, refusedPostHadToken: boolean, outcome: RetryOutcome) {
  traceTurnEvent({ kind: 'challenge:retry', chatId: post.chatId, turnId: post.turnId, refusedPostHadToken, outcome })
}
